import { Table as BaseTable } from 'components'
import {
  differenceInHours,
  differenceInMinutes,
  format,
  parseISO,
} from 'date-fns'
import React from 'react'
import styled from 'styled-components'
import { useQuery } from '@apollo/client'
import { USER_TIME_ENTRIES_QUERY } from '../queries'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useNodes } from 'utils/hooks'

const Wrapper = styled.div`
  margin-top: 2rem;

  & > h2 {
    margin: 1rem 0 0 8px;
    font-weight: 600;
  }
`

const Table = styled(BaseTable)`
  position: relative;
  margin-top: 1rem;

  ${props => props.theme.media.largePad}
  ${props => props.theme.media.mobile} {
    overflow: auto;
  }

  td.absence-info {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 12px 1rem;
    height: 56px;

    span {
      color: ${props => props.theme.colors.gray5};
      font-weight: 600;
    }
  }
`

interface TimesheetsTableProps {
  startDate: Date
  endDate: Date
  userId: string
}

export const SimpleTimesheetsTable: React.FC<TimesheetsTableProps> = ({
  startDate,
  endDate,
  userId,
}) => {
  const translations = useTranslate({
    start: 'common.start',
    end: 'common.end',
    duration: 'common.duration',
    timeEntriesThisPeriod: 'timesheets.time-entries-this-period',
  })
  const { data, loading } = useQuery(USER_TIME_ENTRIES_QUERY, {
    variables: {
      datetimeEndGt: startDate.toISOString(),
      datetimeEndLt: endDate.toISOString(),
      user: userId,
    },
  })

  const timeEntries = useNodes(data?.allTimeEntries)

  return !loading && timeEntries.length > 0 ? (
    <Wrapper>
      <h2>{translations.timeEntriesThisPeriod}</h2>
      <Table headerMargin rowHoverBackground="gray10">
        <thead>
          <tr>
            <th>{translations.start}</th>
            <th>{translations.end}</th>
            <th>{translations.duration}</th>
          </tr>
        </thead>
        <tbody>
          {timeEntries.map(entry => {
            const start = parseISO(entry.datetimeStart)
            const end = entry.datetimeEnd ? parseISO(entry.datetimeEnd) : null
            const diffHours = differenceInHours(
              end ? new Date(end) : new Date(start),
              new Date(start)
            )
            const diffMinutes =
              differenceInMinutes(
                end ? new Date(end) : new Date(start),
                new Date(start)
              ) % 60
            const duration =
              diffHours + diffMinutes > 0
                ? `${diffHours.toString().padStart(2, '0')}:${diffMinutes
                    .toString()
                    .padStart(2, '0')}`
                : null

            return (
              <tr>
                <td>{format(start, 'dd. MMM HH:mm')}</td>
                <td>{end ? format(end, 'dd. MMM HH:mm') : '-'}</td>
                <td>{duration ? duration : '-'}</td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </Wrapper>
  ) : (
    <></>
  )
}
